import { getHallData, getMultiTableDetail } from '@/api'
import router, { ROUTES } from '@/router'
import { store } from '@/store'
import { UserMutation } from '@/store/module/user'
import { EventBuss, SOCKET_MESSAGE } from '@/utils/constants'
import axios from 'axios'
import { ResultMemberList, SocketMessage, Table } from 'vue'
import { Options, Vue } from 'vue-class-component'
import { useI18n } from 'vue-i18n'
import { Action, Getter } from 'vuex-class'
import {
    buildTableEntry,
    collectGoodRoadTableIds,
    countGoodRoadTables,
    GOOD_ROAD_GAME_TYPES,
    GoodRoadTableEntry,
    mergeMultiDetailIntoTables,
    mergeTableBetUpdate,
    sortTables,
    splitGoodRoadTablesByType,
    TableSortMode
} from './utils'
import { GoodRoadMutation } from '@/store/module/good-road'
import type { DesksByType } from '@/store/module/good-road'

@Options({})
export class GoodRoadMixin extends Vue {
    iN = useI18n().t

    selectedIndex = 'G002'
    desksByType: Record<string, Table[]> = {
        G002: [],
        G003: []
    }
    serviceTimer: number | boolean = false
    countDownTimer: number | boolean = false
    timerStarted = false
    failureCounter = 0
    sortMode: TableSortMode = 'goodRoad'
    showSortMenu = false

    private onSortMenuOutsideClick = (): void => {
        this.showSortMenu = false
    }

    emitter!: Vue['emitter']
    $emit!: Vue['$emit']

    @Action('hideLoading') hideLoading!: () => void
    @Action('showLoading') showLoading!: () => void
    @Getter('token') token!: string
    @Getter('memberId') memberId!: number
    @Getter('limitId') limitId!: number
    @Getter('goodRoadDesksByType') storedDesksByType!: DesksByType
    @Getter('hasGoodRoadHallData') hasGoodRoadHallData!: boolean
    @Getter('pinnedTableIds') pinnedTableIds!: number[]

    beforeMount(): void {
        this.emitter.on(EventBuss.SOCKET_MESSAGE, this.handlesSocketMessages)
        this.emitter.on(EventBuss.PAGE_ON_VISIBLE, this.onPageVisible)
        this.emitter.on(
            EventBuss.SOCKET_RECONNECTED,
            this.handleReconnectSuccess
        )
    }

    mounted(): void {
        const params = new URLSearchParams(window.location.search)
        const gameType = params.get('gameType')
        if (gameType) this.selectedIndex = gameType
        this.loadAllData()
        document.addEventListener('click', this.onSortMenuOutsideClick)
    }

    beforeUnmount(): void {
        document.removeEventListener('click', this.onSortMenuOutsideClick)
        this.stopApiRequest()
        this.cancelCountDown()
        this.emitter.off(EventBuss.SOCKET_MESSAGE, this.handlesSocketMessages)
        this.emitter.off(EventBuss.PAGE_ON_VISIBLE, this.onPageVisible)
        this.emitter.off(
            EventBuss.SOCKET_RECONNECTED,
            this.handleReconnectSuccess
        )
    }

    onPageVisible(): void {
        // Prefer store + multi detail; avoid redundant hall unless empty.
        this.loadAllData(false)
    }

    handleReconnectSuccess(): void {
        // After reconnect, refresh hall inventory in case tables were missed.
        this.loadAllData(true)
    }

    handlesSocketMessages(_message: SocketMessage | any): void {
        const _type = _message.type?.toUpperCase()
        const sDesk = _message.gameTable as Table | undefined
        if (!sDesk) return

        const gameType = sDesk.gameTypeNo
        if (gameType !== 'G002' && gameType !== 'G003') return

        const desks = this.desksByType[gameType] || []
        const index = desks.findIndex(item => item.id === sDesk.id)
        if (index === -1) return

        const updated = this.mergeSocketTable(desks[index], sDesk, _type)
        if (!updated) return

        desks.splice(index, 1, updated)
        this.syncDesksToStore()
        this.emitGoodRoadSync()
    }

    mergeSocketTable(current: Table, sDesk: Table, type: string): Table | null {
        const _copy = { ...current, expand: { ...current.expand } }

        if (type === SOCKET_MESSAGE.NEW_GAME) {
            _copy.expand.shoeNo = sDesk.expand.shoeNo
            _copy.expand.juNo = sDesk.expand.juNo
            _copy.expand.gameNo = sDesk.expand.gameNo
            _copy.expand.daoJiShiBetTime = sDesk.expand.daoJiShiBetTime
            _copy.expand.stopBet = 0
            _copy.expand.betFormList = []
            _copy.expand.xianAmount = 0
            _copy.expand.xianDuiAmount = 0
            _copy.expand.zhuangAmount = 0
            _copy.expand.zhuangDuiAmount = 0
            _copy.expand.heAmount = 0
            _copy.expand.playerOne = undefined
            _copy.expand.playerTwo = undefined
            _copy.expand.playerThree = undefined
            _copy.expand.bankerOne = undefined
            _copy.expand.bankerTwo = undefined
            _copy.expand.bankerThree = undefined
            _copy.expand.playerCount = undefined
            _copy.expand.bankerCount = undefined
            _copy.expand.whoWin = -1
            _copy.timeStamp = new Date().getTime()
            return _copy
        }

        if (type === SOCKET_MESSAGE.END_GAME) {
            _copy.expand.playerOne = sDesk.expand.playerOne
            _copy.expand.playerTwo = sDesk.expand.playerTwo
            _copy.expand.playerThree = sDesk.expand.playerThree
            _copy.expand.bankerOne = sDesk.expand.bankerOne
            _copy.expand.bankerTwo = sDesk.expand.bankerTwo
            _copy.expand.bankerThree = sDesk.expand.bankerThree
            _copy.expand.playerCount = sDesk.expand.playerCount
            _copy.expand.bankerCount = sDesk.expand.bankerCount
            _copy.expand.whoWin = sDesk.expand.whoWin
            _copy.expand.resultList = sDesk.expand.resultList
            _copy.expand.winOrLoseList = sDesk.expand.winOrLoseList
            _copy.expand.betFormList = []
            _copy.expand.xianAmount = 0
            _copy.expand.xianDuiAmount = 0
            _copy.expand.zhuangAmount = 0
            _copy.expand.zhuangDuiAmount = 0
            _copy.expand.heAmount = 0
            _copy.timeStamp = new Date().getTime()
            return _copy
        }

        if (type === SOCKET_MESSAGE.BET) {
            if (sDesk.expand.memberId !== this.memberId) return null

            const merged = mergeTableBetUpdate(_copy, sDesk)
            const balance = merged.expand?.amount
            if (balance !== undefined && balance !== null) {
                store.commit(UserMutation.SET_BET_COIN, balance)
            }
            return merged
        }

        if (type === SOCKET_MESSAGE.STOP_GAME) {
            _copy.expand.daoJiShiBetTime = 0
            _copy.status = 2
            return _copy
        }

        if (type === SOCKET_MESSAGE.STOP_BET) {
            _copy.expand.stopBet = 1
            if (
                sDesk.expand.amount !== undefined &&
                sDesk.expand.amount !== null
            ) {
                store.commit(UserMutation.SET_BET_COIN, sDesk.expand.amount)
            }
            return _copy
        }

        if (type === SOCKET_MESSAGE.CANCEL_GAME) {
            _copy.expand.betFormList = []
            _copy.status = sDesk.status

            const memberResultList = sDesk.expand.winOrLoseList as
                | ResultMemberList[]
                | undefined
            const mine = memberResultList?.find(
                x => x.memberId === this.memberId
            )
            if (mine) {
                store.commit(UserMutation.SET_BET_COIN, mine.amount)
            }
            return _copy
        }

        if (type === SOCKET_MESSAGE.NEW_SHOE) {
            sDesk.expand.betFormList = []
            const {
                downLimit,
                upLimit,
                limitMax,
                pairDownLimit,
                pairUpLimit,
                tieDownLimit,
                tieUpLimit,
                superSixDownLimit,
                superSixUpLimit
            } = _copy

            sDesk.downLimit = downLimit
            sDesk.upLimit = upLimit
            sDesk.limitMax = limitMax
            sDesk.pairDownLimit = pairDownLimit
            sDesk.pairUpLimit = pairUpLimit
            sDesk.tieDownLimit = tieDownLimit
            sDesk.tieUpLimit = tieUpLimit
            sDesk.superSixDownLimit = superSixDownLimit
            sDesk.superSixUpLimit = superSixUpLimit
            sDesk.expand.resultList = []

            return {
                ..._copy,
                ...sDesk,
                expand: {
                    ..._copy.expand,
                    ...sDesk.expand
                }
            }
        }

        return null
    }

    hideMe(): void {
        this.$emit('on-hide-good-road')
    }

    changeIndex(gameType: string): void {
        if (gameType === this.selectedIndex) return
        this.selectedIndex = gameType
    }

    loadAllData(forceReload = false): void {
        if (!forceReload) this.showLoading()

        const loadPromise = forceReload || !this.hasGoodRoadHallData
            ? this.fetchAllHallData().then(() => this.fetchAndMergeMultiDetail())
            : this.hydrateFromStore().then(() => this.fetchAndMergeMultiDetail())

        loadPromise
            .then(() => {
                if (!forceReload) this.hideLoading()
                this.syncDesksToStore()
                this.emitGoodRoadSync()
                this.cancelCountDown()
                this.startCountDown()
            })
            .catch(() => {
                if (!forceReload) this.hideLoading()
            })
    }

    clickedItem(desk: Table): void {

        if (router.currentRoute.value.name === ROUTES.GAME) {
            router.replace({
                query: {
                    desk: desk.id,
                    gameType: desk.gameTypeNo
                }
            })
        } else {
            router.push({
                name: ROUTES.GAME,
                query: {
                    desk: desk.id,
                    gameType: desk.gameTypeNo
                }
            })
        }

       
    }

    hydrateFromStore(): Promise<void> {
        const stored = this.storedDesksByType || { G002: [], G003: [] }
        this.desksByType = {
            G002: [...(stored.G002 || [])],
            G003: [...(stored.G003 || [])]
        }
        return Promise.resolve()
    }

    syncDesksToStore(): void {
        store.commit(GoodRoadMutation.SET_DESKS_BY_TYPE, {
            G002: this.desksByType.G002 || [],
            G003: this.desksByType.G003 || []
        })
    }

    fetchAndMergeMultiDetail(): Promise<void> {
        const tableIds = collectGoodRoadTableIds(this.desksByType)
        if (!tableIds.length) return Promise.resolve()

        return getMultiTableDetail({
            token: this.token,
            memberId: this.memberId,
            tableIds: tableIds.join(','),
            limitId: this.limitId
        })
            .then(r => {
                const response = r.data
                if (response.status !== 200) {
                    if (response.status === 301) {
                        this.stopApiRequest()
                        this.emitter.emit(EventBuss.SESSION_EXPIRED)
                    }
                    return
                }

                const detailTables = response.data.gameTableList || []
                if (!detailTables.length) return

                GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
                    this.desksByType[gameTypeNo] = mergeMultiDetailIntoTables(
                        this.desksByType[gameTypeNo] || [],
                        detailTables
                    )
                })
            })
            .catch(error => {
                if (axios.isCancel(error)) return
                throw error
            })
    }

    fetchAllHallData(): Promise<void> {
        return getHallData({
            token: this.token,
            gameTypeNo: ''
        })
            .then(r => {
                const response = r.data
                if (response.status === 200) {
                    this.failureCounter = 0

                    if (response.data.memberLimitList) {
                        store.commit(
                            UserMutation.SET_USER_INFO_LIMITS,
                            response.data.memberLimitList
                        )
                    }

                    this.desksByType = splitGoodRoadTablesByType(
                        response.data.gameTableList || []
                    )
                    store.commit(GoodRoadMutation.SET_HALL_TABLES, {
                        tables: response.data.gameTableList || [],
                        gameTypeNo: ''
                    })
                    return
                }

                if (response.status === 301) {
                    this.stopApiRequest()
                    this.emitter.emit(EventBuss.SESSION_EXPIRED)
                }

                GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
                    this.desksByType[gameTypeNo] = []
                })
            })
            .catch(error => {
                if (axios.isCancel(error)) return

                this.failureCounter++
                this.emitter.emit(
                    EventBuss.TOAST_MESSAGE,
                    this.iN('prompt.networkerror')
                )

                if (this.failureCounter > 3) {
                    this.emitter.emit(
                        EventBuss.TOAST_MESSAGE,
                        this.iN('prompt.networkerror')
                    )
                    this.emitter.emit(EventBuss.SOCKET_CLOSE)
                    store.commit(UserMutation.DELETE_SESSION)
                    router.push({ name: ROUTES.LOGIN })
                }

                throw error
            })
    }

    startCountDown(): void {
        if (typeof this.countDownTimer === 'number') return

        this.timerStarted = true
        this.doCountDown()
    }

    doCountDown(): void {
        this.countDownTimer = window.setTimeout(() => {
            GOOD_ROAD_GAME_TYPES.forEach(gameTypeNo => {
                const desks = this.desksByType[gameTypeNo] || []

                desks.forEach(item => {
                    if (item.expand.daoJiShiBetTime > 0) {
                        item.expand.daoJiShiBetTime -= 1
                    } else if (item.expand.daoJiShiBetTime === 0) {
                        item.expand.daoJiShiBetTime = -1
                        item.status = 2
                    }
                })
            })

            this.doCountDown()
        }, 1000)
    }

    cancelCountDown(): void {
        if (typeof this.countDownTimer === 'number') {
            clearTimeout(this.countDownTimer)
            this.countDownTimer = false
            this.timerStarted = false
        }
    }

    loadData(forceReload = false): void {
        this.loadAllData(forceReload)
    }

    stopApiRequest(): void {
        this.cancelCountDown()
        if (typeof this.serviceTimer === 'number') {
            clearTimeout(this.serviceTimer)
            this.serviceTimer = false
        }
    }

    emitGoodRoadSync(): void {
        this.$emit('on-good-road-sync', {
            G002: this.desksByType.G002 || [],
            G003: this.desksByType.G003 || []
        })
    }

    toggleSortMenu(): void {
        this.showSortMenu = !this.showSortMenu
    }

    setSortMode(mode: TableSortMode): void {
        this.sortMode = mode
        this.showSortMenu = false
    }

    togglePin(tableId: number): void {
        store.commit(GoodRoadMutation.TOGGLE_PINNED_TABLE, tableId)
    }

    get sortLabel(): string {
        return this.sortMode === 'goodRoad'
            ? this.iN('common.goodroadsort')
            : this.iN('common.tablesort')
    }

    get listData(): GoodRoadTableEntry[] {
        const sorted = sortTables(
            this.desksByType[this.selectedIndex] || [],
            this.sortMode
        )
        const entries = sorted.map(table => buildTableEntry(table))
        return this.placePinnedEntriesFirst(entries)
    }

    placePinnedEntriesFirst(
        entries: GoodRoadTableEntry[]
    ): GoodRoadTableEntry[] {
        const pinned = this.pinnedTableIds || []
        if (!pinned.length) return entries

        const byId = new Map(entries.map(entry => [entry.table.id, entry]))
        const pinnedSet = new Set(pinned)
        const pinnedEntries: GoodRoadTableEntry[] = []

        pinned.forEach(id => {
            const entry = byId.get(id)
            if (entry) pinnedEntries.push(entry)
        })

        return pinnedEntries.concat(
            entries.filter(entry => !pinnedSet.has(entry.table.id))
        )
    }

    get baccaratGoodRoadCount(): number {
        return countGoodRoadTables(this.desksByType.G002 || [])
    }

    get dragonTigerGoodRoadCount(): number {
        return countGoodRoadTables(this.desksByType.G003 || [])
    }

    onItemBetSuccess(table: Table): void {
        const gameType = table.gameTypeNo
        if (gameType !== 'G002' && gameType !== 'G003') return

        const desks = this.desksByType[gameType] || []
        const index = desks.findIndex(d => d.id === table.id)
        if (index !== -1) {
            desks.splice(index, 1, mergeTableBetUpdate(desks[index], table))

            const balance = table.expand?.amount
            if (balance !== undefined && balance !== null) {
                store.commit(UserMutation.SET_BET_COIN, balance)
            }
        }

        this.syncDesksToStore()
        this.emitGoodRoadSync()
    }
}
